"use client";

import React from "react";
import { BadgeCheck, ShieldCheck, FileText, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function KlarifikasiResmiConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-8">
        {[...Array(2)].map((_, i) => <div key={i} data-index={i} className="h-56 bg-muted/30 animate-pulse border-t-[6px] border-t-foreground/20" />)}
      </div>;
  }
  if (error) {
    return <div className="p-6 border border-destructive/20 bg-destructive/5 text-destructive text-sm">
        Gagal memuat data klarifikasi.
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-muted-foreground text-sm">Belum ada klarifikasi resmi yang diterbitkan.</p>;
  }
  return <div className="space-y-8">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="border-x-0 border-b-0 border-t-[6px] border-t-foreground shadow-sm bg-card hover:bg-muted/10 transition-colors">
          <CardContent className="p-5 sm:p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <BadgeCheck className="size-5 text-primary" />
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Klarifikasi Terverifikasi
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold tracking-tight leading-snug mb-4 font-serif">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </h3>
            <div className="text-sm md:text-base text-muted-foreground line-clamp-3 mb-6 leading-relaxed" dangerouslySetInnerHTML={{
          __html: post.excerpt || "Tidak ada ringkasan tersedia."
        }} />
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between pt-4 border-t border-border/50">
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                {post.date ? new Date(post.date).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }) : ''}
              </span>
              <Link to={post.link || "#"}>
                <Button variant="link" className="px-0 text-primary font-semibold tracking-tight h-auto py-0">
                  Baca Selengkapnya &rarr;
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>)}
    </div>;
}
function HakJawabConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col divide-y divide-border border-y border-border">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="py-5 h-24 bg-muted/20 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-destructive">
        Gagal memuat data hak jawab.
      </div>;
  }
  if (posts.length === 0) {
    return <p className="text-muted-foreground text-sm">Belum ada data hak jawab atau cek fakta.</p>;
  }
  return <div className="flex flex-col divide-y divide-border border-y border-border">
      {posts.map((post, i) => <div key={post.id} data-index={i} className="py-4 sm:py-5 group">
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
            <ShieldCheck className="size-6 text-primary shrink-0 mt-0.5" />
            <div>
              <h4 className="text-base md:text-lg font-semibold tracking-tight leading-snug mb-2 group-hover:text-primary transition-colors">
                <Link to={post.link || "#"}>{post.title}</Link>
              </h4>
              <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono uppercase tracking-wider">
                <span>
                  {post.date ? new Date(post.date).toLocaleDateString('id-ID', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              }) : ''}
                </span>
                <span className="w-1 h-1 rounded-lg bg-border"></span>
                <span className="text-primary font-medium">Cek Fakta</span>
              </div>
            </div>
          </div>
        </div>)}
    </div>;
}
export default function Clarification() {
  const wp_query_params_left = {
    per_page: 2,
    orderby: "date",
    order: "desc",
    post_type: 'post',
    status: 'publish'
  };
  const wp_query_left = new WP_Query(wp_query_params_left);
  const wp_query_params_right = {
    per_page: 5,
    orderby: "date",
    order: "desc",
    post_type: 'post',
    status: 'publish'
  };
  const wp_query_right = new WP_Query(wp_query_params_right);
  return <section id="klarifikasi" className="bg-background py-16 lg:py-24 border-t border-border">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter uppercase border-b-[3px] border-foreground pb-4 inline-block font-serif">
            Buku Putih
          </h2>
          <p className="text-muted-foreground mt-4 text-base md:text-lg max-w-2xl tracking-tight">
            Dokumentasi resmi klarifikasi, hak jawab, dan cek fakta atas berbagai isu dan pemberitaan yang beredar di masyarakat.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12 lg:gap-16">
          <div className="lg:col-span-7 xl:col-span-8">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <FileText className="size-6 text-foreground" />
              <h3 className="text-lg sm:text-xl font-bold uppercase tracking-widest text-foreground">
                Klarifikasi Resmi
              </h3>
            </div>
            <PostsProvider wp_query={{
            per_page: 2,
            orderby: "date",
            order: "desc",
            post_type: "post",
            status: ["publish"]
          }}>
              <KlarifikasiResmiConsumer />
            </PostsProvider>
          </div>

          <div className="lg:col-span-5 xl:col-span-4">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <AlertCircle className="size-6 text-foreground" />
              <h3 className="text-lg sm:text-xl font-bold uppercase tracking-widest text-foreground">
                Hak Jawab & Cek Fakta
              </h3>
            </div>
            <PostsProvider wp_query={{
            per_page: 5,
            orderby: "date",
            order: "desc",
            post_type: "post",
            status: ["publish"]
          }}>
              <HakJawabConsumer />
            </PostsProvider>
            
            <div className="mt-6 md:mt-8">
              <Link to="#">
                <Button variant="outline" className="w-full uppercase tracking-widest font-bold text-xs h-12 border-2">
                  Lihat Semua Arsip
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}